<script>
import { fileSize as formatFileSize } from '@utils/Format'
import { ICONS } from '@modules/atendimento/constants/Text'

export default {
  name: 'ChatMediaUploadPreview',
  props: {
    file: {
      type: [Object, File],
      default: null,
    },
    progress: {
      type: Number,
      default: null,
    },
    caption: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      icons: ICONS,
      thumbnailUrl: null,
    }
  },
  computed: {
    fileName() {
      if (!this.file) return ''
      return this.file.name || 'Arquivo'
    },
    fileSize() {
      if (!this.file || !this.file.size) return ''
      return formatFileSize(this.file.size)
    },
    isImage() {
      return this.file && this.file.type && this.file.type.startsWith('image/')
    },
    isVideo() {
      return this.file && this.file.type && this.file.type.startsWith('video/')
    },
    isAudio() {
      return this.file && this.file.type && this.file.type.startsWith('audio/')
    },
    isUploading() {
      return this.progress !== null && this.progress < 100
    },
    uploadComplete() {
      return this.progress !== null && this.progress >= 100
    },
    fileIcon() {
      if (this.isAudio) return ICONS.musicNote
      return ICONS.fileDocument
    },
  },
  watch: {
    file: {
      immediate: true,
      handler: function (newFile) {
        this.createThumbnail(newFile)
      },
    },
  },
  beforeDestroy() {
    this.revokeThumbnail()
  },
  methods: {
    createThumbnail(file) {
      this.revokeThumbnail()
      if (!file) return
      if (this.isImage || this.isVideo) {
        try {
          this.thumbnailUrl = URL.createObjectURL(file)
        } catch {
          // Browsers podem recusar createObjectURL em File inválido
          // (quota, permissão de sandbox). Preview silenciosa fallback.
          this.thumbnailUrl = null
        }
      }
    },
    revokeThumbnail() {
      if (this.thumbnailUrl) {
        try {
          URL.revokeObjectURL(this.thumbnailUrl)
        } catch {
          // revokeObjectURL pode falhar se a URL já foi invalidada pelo GC.
          // Seguro ignorar — só garantindo que o ref local seja limpo.
        }
        this.thumbnailUrl = null
      }
    },
    onCancel() {
      this.$emit('cancel')
    },
    onCaptionInput(event) {
      this.$emit('update:caption', event.target.value)
    },
    onCaptionKeydown(event) {
      if (event.key === 'Enter') {
        this.$emit('send')
      }
    },
    focus() {
      const el = this.$refs.captionInput
      if (el) el.focus()
    },
  },
}
</script>

<template>
  <div
    v-if="file"
    data-test="media-upload-preview"
    class="chat-media-upload-preview pa-2"
  >
    <!-- Cancel no canto superior direito (#8E8E8E, menor) -->
    <v-btn
      :disabled="isUploading"
      data-test="media-cancel"
      icon
      x-small
      class="cancel-btn"
      @click="onCancel"
    >
      <icon-svg :name="icons.close" size="12" color="#8E8E8E" />
    </v-btn>

    <div class="d-flex align-center">
      <div class="preview-thumb mr-3">
        <img
          v-if="isImage && thumbnailUrl"
          :src="thumbnailUrl"
          :alt="fileName"
          class="preview-image rounded"
        />
        <video
          v-else-if="isVideo && thumbnailUrl"
          :src="thumbnailUrl"
          class="preview-image rounded"
        >
          <track kind="captions" />
        </video>
        <div
          v-else
          class="preview-icon d-flex align-center justify-center rounded"
        >
          <icon-svg :name="fileIcon" size="24" color="grey darken-1" />
        </div>
      </div>

      <div class="flex-grow-1 overflow-hidden pr-6">
        <div class="az-text-primary az-size-12 font-medium text-truncate">
          {{ fileName }}
        </div>
        <div class="az-text-secondary az-size-12">
          {{ fileSize }}
        </div>
        <input
          ref="captionInput"
          :value="caption"
          data-test="media-caption"
          type="text"
          placeholder="Adicionar legenda..."
          class="caption-input az-text-primary az-size-12 mt-1"
          @input="onCaptionInput"
          @keydown="onCaptionKeydown"
        />
      </div>
    </div>

    <div v-if="progress !== null" class="mt-2">
      <v-progress-linear
        :value="progress"
        data-test="upload-progress"
        height="4"
        rounded
        :color="uploadComplete ? 'success' : 'primary'"
      />
    </div>
  </div>
</template>

<style lang="sass" scoped>
.chat-media-upload-preview
  position: relative
  // Respeita o tema do contexto (flatGray em view-atendimento,
  // flatWhite em omni-atendimento) via --panel-bg.
  background-color: var(--panel-bg)
  border-top: 1px solid var(--gray-3)

.preview-thumb
  width: 56px
  height: 56px
  flex-shrink: 0

.preview-image
  width: 56px
  height: 56px
  object-fit: cover

.preview-icon
  width: 56px
  height: 56px
  background-color: rgba(0, 0, 0, 0.06)

.caption-input
  width: 100%
  border: none
  border-bottom: 1px solid var(--gray-3)
  background: transparent
  padding: 2px 0
  outline: none

.caption-input:focus
  border-bottom-color: var(--primary)

.cancel-btn
  position: absolute
  top: 6px
  right: 6px

.cancel-btn:hover
  background-color: rgba(0, 0, 0, 0.08)

.cancel-btn:disabled
  opacity: 0.4
  cursor: not-allowed
</style>
